import { useEffect, useState } from "react";
import {
  COLS,
  CONFIRM_BTN,
  CYCLE,
  DOC_LANDING,
  DROPZONE,
  Frame,
  HOLD,
  QR_CENTER,
  STORY_END,
  SWITCH_DOC,
  SWITCH_MEIA,
  TIER_CARD,
  colCenter,
  computeFrame,
} from "./half-price-flow-timeline";

/* Storyboard da meia-entrada com documento comprobatório. Três colunas —
   produtor, comprador e portaria — e um cursor que atravessa as três:
   1. o produtor liga a meia-entrada no lote e passa a exigir documento;
   2. o comprador escolhe o lote e ganha uma etapa extra no checkout;
   3. o documento é enviado e só então a compra conclui;
   4. na portaria o mesmo documento é conferido antes de liberar o acesso.
   O ponto que a UI não conta: enviar no checkout não dispensa apresentar
   o documento na entrada. */

const AMBER = "#B45309";
const AMBER_BG = "#F59E0B";
const GREEN = "hsl(142 71% 45%)";
const GREEN_TEXT = "hsl(142 71% 32%)";

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (ev: MediaQueryListEvent) => setReduced(ev.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
}

function Switch({ on, cx, cy }: { on: boolean; cx: number; cy: number }) {
  return (
    <g>
      <rect
        x={cx - 13}
        y={cy - 8}
        width={26}
        height={16}
        rx={8}
        style={{ fill: on ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))", transition: "fill 0.3s ease" }}
        opacity={on ? 1 : 0.35}
      />
      <circle
        cy={cy}
        r={6}
        className="fill-background"
        style={{
          transform: `translateX(${on ? cx + 5 : cx - 5}px)`,
          transition: "transform 0.3s cubic-bezier(0.22,1,0.36,1)",
        }}
      />
    </g>
  );
}

function DocCard({ x, y, scale = 1, opacity = 1 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <rect x={-40} y={-11} width={80} height={22} rx={6} fill="hsl(var(--background))" stroke="hsl(var(--border))" />
      <rect x={-33} y={-6} width={9} height={12} rx={2} fill="hsl(var(--primary))" opacity={0.18} />
      <path d="M-30.5 -2.5 h4 M-30.5 0.5 h4 M-30.5 3 h2.5" stroke="hsl(var(--primary))" strokeWidth={0.9} strokeLinecap="round" />
      <text x={-19} y={3} className="text-[7px] font-medium" fill="hsl(var(--foreground))">
        documento.jpg
      </text>
    </g>
  );
}

function Check({ x, y, r = 7, color = GREEN }: { x: number; y: number; r?: number; color?: string }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle r={r} fill={color} opacity={0.15} />
      <path
        d={`M${-r * 0.4} 0 l${r * 0.28} ${r * 0.3} l${r * 0.55} -${r * 0.62}`}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
}

export function HalfPriceFlowDemo() {
  const reduced = usePrefersReducedMotion();
  const [frame, setFrame] = useState<Frame>(() => computeFrame(0));

  useEffect(() => {
    if (reduced) {
      setFrame(computeFrame(STORY_END + HOLD / 2));
      return;
    }
    let raf = 0;
    let start: number | null = null;
    const loop = (ts: number) => {
      if (start === null) start = ts;
      setFrame(computeFrame((ts - start) % CYCLE));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [reduced]);

  const {
    cursor,
    press,
    clickPulse,
    showCursor,
    meiaOn,
    docOn,
    docRowIn,
    tierPicked,
    stepIn,
    fileIn,
    uploadProgress,
    uploadDone,
    confirmOn,
    qrPulse,
    badgeIn,
    docFly,
    accessOk,
    focus,
    stage,
  } = frame;

  const cursorScale = 1 - press * 0.2;
  const colOpacity = (i: number) => (reduced ? 1 : i === focus ? 1 : 0.45);

  // documento viajando do checkout para a portaria
  const flying = docFly > 0.001 && docFly < 0.999;
  const flyX = DROPZONE.x + (DOC_LANDING.x - DROPZONE.x) * docFly;
  const flyY = DROPZONE.y + (DOC_LANDING.y - DROPZONE.y) * docFly - Math.sin(docFly * Math.PI) * 30;

  return (
    <div className="w-full rounded-3xl border border-border/40 bg-secondary/20 p-3 sm:p-4 overflow-hidden">
      <svg viewBox="0 0 520 276" className="w-full">
        <defs>
          <filter id="hpShadow" x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="3" stdDeviation="4" floodOpacity="0.16" />
          </filter>
        </defs>

        <g opacity={reduced ? 1 : stage}>
          {/* ── Colunas ── */}
          {COLS.map((c, i) => (
            <g key={c.title} style={{ opacity: colOpacity(i), transition: "opacity 0.45s ease" }}>
              <rect
                x={c.x}
                y={14}
                width={c.w}
                height={248}
                rx={12}
                fill="hsl(var(--background))"
                stroke={i === focus ? "hsl(var(--border))" : "hsl(var(--border))"}
                strokeOpacity={i === focus ? 1 : 0.5}
                strokeWidth={1.5}
              />
              <text x={c.x + 12} y={35} className="text-[9px] font-bold" fill="hsl(var(--foreground))">
                {c.title}
              </text>
              <text x={c.x + 12} y={46} className="text-[7px]" fill="hsl(var(--muted-foreground))">
                {i === 0 ? "ao criar o lote" : i === 1 ? "no checkout" : "no dia do evento"}
              </text>
              <line x1={c.x + 12} y1={56} x2={c.x + c.w - 12} y2={56} stroke="hsl(var(--border))" strokeOpacity={0.6} />
            </g>
          ))}

          {/* setas entre as colunas */}
          {[176, 342].map((x, i) => (
            <path
              key={x}
              d={`M${x - 2} 134 l4 4 l-4 4`}
              fill="none"
              stroke="hsl(var(--muted-foreground))"
              strokeWidth={1.3}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ opacity: focus > i ? 0.85 : 0.25, transition: "opacity 0.4s ease" }}
            />
          ))}

          {/* ── Coluna 1: produtor ── */}
          <g style={{ opacity: colOpacity(0), transition: "opacity 0.45s ease" }}>
            <text x={26} y={100} className="text-[7.5px] font-semibold" fill="hsl(var(--foreground))">
              Meia-entrada
            </text>
            <text x={26} y={110} className="text-[6.5px]" fill="hsl(var(--muted-foreground))">
              neste lote
            </text>
            <Switch on={meiaOn} cx={SWITCH_MEIA.x} cy={SWITCH_MEIA.y} />

            <g style={{ opacity: docRowIn, transition: "opacity 0.3s ease" }}>
              <text x={26} y={142} className="text-[7.5px] font-semibold" fill="hsl(var(--foreground))">
                Exigir documento
              </text>
              <text x={26} y={152} className="text-[6.5px]" fill="hsl(var(--muted-foreground))">
                no checkout
              </text>
              <Switch on={docOn} cx={SWITCH_DOC.x} cy={SWITCH_DOC.y} />
            </g>

            <g style={{ opacity: docOn ? 1 : 0, transition: "opacity 0.4s ease" }}>
              <rect x={26} y={186} width={134} height={30} rx={7} fill={AMBER_BG} opacity={0.1} />
              <text x={34} y={198} className="text-[6.5px] font-semibold" fill={AMBER}>
                Lei 12.933/2013
              </text>
              <text x={34} y={208} className="text-[6.5px]" fill={AMBER}>
                exige o comprovante
              </text>
            </g>
          </g>

          {/* ── Coluna 2: comprador ── */}
          <g style={{ opacity: colOpacity(1), transition: "opacity 0.45s ease" }}>
            {/* lote escolhido */}
            <rect
              x={192}
              y={86}
              width={134}
              height={28}
              rx={7}
              fill="hsl(var(--background))"
              stroke={tierPicked ? "hsl(var(--primary))" : "hsl(var(--border))"}
              strokeWidth={tierPicked ? 1.6 : 1}
              style={{ transition: "stroke 0.3s ease" }}
            />
            <circle
              cx={204}
              cy={100}
              r={4.5}
              fill="none"
              stroke={tierPicked ? "hsl(var(--primary))" : "hsl(var(--border))"}
              strokeWidth={1.4}
            />
            {tierPicked && <circle cx={204} cy={100} r={2.2} fill="hsl(var(--primary))" />}
            <text x={214} y={97} className="text-[7.5px] font-semibold" fill="hsl(var(--foreground))">
              Meia-entrada
            </text>
            <text x={214} y={107} className="text-[6.5px]" fill="hsl(var(--muted-foreground))">
              estudante
            </text>
            <text x={318} y={103} textAnchor="end" className="text-[8px] font-bold tabular-nums" fill="hsl(var(--primary))">
              R$ 30
            </text>

            {/* etapa extra que aparece no fluxo */}
            <g style={{ opacity: stepIn, transition: "opacity 0.3s ease" }}>
              <rect x={192} y={122} width={134} height={16} rx={8} fill="hsl(var(--primary))" opacity={0.1} />
              <text x={259} y={133} textAnchor="middle" className="text-[6.5px] font-semibold" fill="hsl(var(--primary))">
                + 1 etapa: enviar documento
              </text>
            </g>

            {/* área de envio */}
            <rect
              x={192}
              y={146}
              width={134}
              height={36}
              rx={8}
              fill={uploadDone ? "hsl(var(--background))" : "transparent"}
              stroke={uploadDone ? GREEN : "hsl(var(--border))"}
              strokeOpacity={uploadDone ? 1 : 0.8}
              strokeDasharray={uploadDone ? undefined : "4 4"}
              style={{ transition: "stroke 0.35s ease" }}
            />
            {fileIn < 0.05 ? (
              <text x={259} y={167} textAnchor="middle" className="text-[7px] font-medium" fill="hsl(var(--muted-foreground))">
                Enviar documento
              </text>
            ) : (
              <g opacity={fileIn}>
                <DocCard x={259} y={uploadDone ? 158 : 164} scale={0.9} />
                {!uploadDone && (
                  <g transform="translate(219 174)">
                    <rect width={80} height={3} rx={1.5} fill="hsl(var(--border))" />
                    <rect width={80 * uploadProgress} height={3} rx={1.5} fill="hsl(var(--primary))" />
                  </g>
                )}
                {uploadDone && (
                  <>
                    <Check x={214} y={174} r={5} />
                    <text x={224} y={176.5} className="text-[6.5px] font-semibold" fill={GREEN_TEXT}>
                      enviado
                    </text>
                  </>
                )}
              </g>
            )}

            {/* concluir compra */}
            <rect
              x={192}
              y={200}
              width={134}
              height={24}
              rx={8}
              fill="hsl(var(--primary))"
              opacity={uploadDone ? 1 : 0.3}
              style={{ transition: "opacity 0.4s ease" }}
            />
            <text x={259} y={215.5} textAnchor="middle" className="text-[7.5px] font-bold" fill="hsl(var(--primary-foreground))">
              {confirmOn ? "Compra concluída" : "Concluir compra"}
            </text>
          </g>

          {/* ── Coluna 3: portaria ── */}
          <g style={{ opacity: colOpacity(2), transition: "opacity 0.45s ease" }}>
            {/* QR */}
            <rect
              x={QR_CENTER.x - 26}
              y={QR_CENTER.y - 26}
              width={52}
              height={52}
              rx={8}
              fill="hsl(var(--background))"
              stroke="hsl(var(--border))"
            />
            {[0, 1, 2].map((r) =>
              [0, 1, 2].map((c) => (
                <rect
                  key={`${r}-${c}`}
                  x={QR_CENTER.x - 18 + c * 13}
                  y={QR_CENTER.y - 18 + r * 13}
                  width={9}
                  height={9}
                  rx={1.5}
                  fill="hsl(var(--foreground))"
                  opacity={(r + c) % 2 === 0 ? 0.75 : 0.25}
                />
              ))
            )}
            {qrPulse > 0.02 && !reduced && (
              <rect
                x={QR_CENTER.x - 26 - qrPulse * 6}
                y={QR_CENTER.y - 26 - qrPulse * 6}
                width={52 + qrPulse * 12}
                height={52 + qrPulse * 12}
                rx={10}
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth={1.4}
                opacity={0.6 * (1 - qrPulse)}
              />
            )}

            {/* alerta de meia-entrada */}
            <g style={{ opacity: badgeIn, transition: "opacity 0.3s ease" }}>
              <rect x={358} y={146} width={136} height={28} rx={8} fill={AMBER_BG} opacity={0.14} />
              <circle cx={372} cy={160} r={5.5} fill={AMBER_BG} opacity={0.3} />
              <text x={372} y={162.5} textAnchor="middle" className="text-[7px] font-bold" fill={AMBER}>
                !
              </text>
              <text x={384} y={157} className="text-[7px] font-bold" fill={AMBER}>
                Conferir documento
              </text>
              <text x={384} y={167} className="text-[6.5px]" fill={AMBER}>
                o mesmo enviado na compra
              </text>
            </g>

            {/* documento que chegou */}
            {docFly > 0.995 && <DocCard x={DOC_LANDING.x} y={DOC_LANDING.y} scale={0.9} />}

            {/* acesso liberado */}
            <g style={{ opacity: accessOk, transition: "opacity 0.3s ease" }}>
              <rect x={358} y={224} width={136} height={24} rx={8} fill={GREEN} opacity={0.14} />
              <Check x={374} y={236} r={5.5} />
              <text x={386} y={238.5} className="text-[7.5px] font-bold" fill={GREEN_TEXT}>
                Acesso liberado
              </text>
            </g>
          </g>

          {/* documento em voo — atravessa as colunas, então vem por cima */}
          {flying && <DocCard x={flyX} y={flyY} scale={0.9} opacity={1} />}

          {/* ripple do clique */}
          {!reduced && clickPulse > 0 && (
            <circle
              cx={cursor.x}
              cy={cursor.y}
              r={4 + clickPulse * 12}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth={1.5}
              opacity={0.55 * (1 - clickPulse)}
            />
          )}

          {/* cursor */}
          {!reduced && showCursor && (
            <g transform={`translate(${cursor.x} ${cursor.y}) scale(${cursorScale})`}>
              <path
                d="M0,0 L0,20 L5.3,15 L9,22.4 L11.8,21.1 L8.1,13.9 L14,13.6 Z"
                className="fill-background"
                stroke="hsl(var(--foreground))"
                strokeWidth={1.4}
                strokeLinejoin="round"
              />
            </g>
          )}
        </g>
      </svg>
    </div>
  );
}
